import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Animated, View as RNView } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { ToggleSize } from '@opendoor/tokens-studio/dist/bricks-next/types/Components';
import { isWeb, styled, View } from '@tamagui/core';
import { SwitchProps, useSwitch } from '@tamagui/switch-headless';

import { Dollar } from '../../__generated__/icons';
import { Text } from '../Text';

type IconComponentType = typeof Dollar;

const { toggle: toggleTokens } = tokens;

const Frame = styled(View, {
  // Use <button> tag for a11y so space/enter keys toggle the switch.
  // See: https://www.w3.org/WAI/ARIA/apg/patterns/switch/
  tag: 'button',
  // @ts-expect-error - need type="button" to prevent form submission on web
  type: 'button',
  padding: 0,
  flexDirection: 'row',
  alignSelf: 'flex-start',
  backgroundColor: toggleTokens.color.background.element.toggle.default,
  cursor: 'pointer',
  borderStyle: 'solid',
  borderWidth: toggleTokens.borderWidth.element.state.default,
  borderColor: toggleTokens.color.border.element.toggle.state.default,
  outlineStyle: 'none',
  borderRadius: toggleTokens.cornerRadius.element.toggle.large,
  animation: 'accelerateDecelerateFastest',
  animateOnly: isWeb ? ['background-color', 'border-color'] : ['backgroundColor', 'borderColor'],
  focusVisibleStyle: {
    borderColor: toggleTokens.color.border.element.toggle.state.focused,
  },
  variants: {
    frameSize: (size: ToggleSize) => {
      return {
        height: toggleTokens.size.element.onOff[size],
      };
    },

    hasError: {
      true: {
        borderColor: toggleTokens.color.border.element.toggle.state.error,
        'aria-invalid': true,
      },
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        pointerEvents: 'none',
      },
    },
    embedded: {
      true: {
        maxWidth: 80,
      },
      false: {
        minWidth: 320,
      },
    },
  } as const,
});

const Content = styled(View, {
  alignItems: 'center',
  justifyContent: 'center',
  variants: {
    frameSize: (size: ToggleSize) => ({
      width: 140,
      height: toggleTokens.size.element.onOff[size],
      borderRadius: toggleTokens.cornerRadius.element.toggle.large,
    }),
    embedded: {
      true: {
        maxWidth: 40,
      },
      false: {
        minWidth: 140,
      },
    },
  },
});

const Thumb = styled(View, {
  backgroundColor: toggleTokens.color.background.element.toggle.on,
  variants: {
    frameSize: (size: ToggleSize) => {
      return {
        height: toggleTokens.size.element.onOff[size],
        borderRadius: toggleTokens.cornerRadius.element.toggle.large,
      };
    },
    embedded: {
      true: {
        maxWidth: 80,
      },
      false: {
        minWidth: 140,
      },
    },
    hasError: {
      true: {
        backgroundColor: toggleTokens.color.background.element.toggle.state.errorOn,
      },
    },
    disabled: {
      true: {
        backgroundColor: toggleTokens.color.background.element.toggle.state.inactiveOn,
      },
    },
  } as const,
});

export type ToggleProps = SwitchProps & {
  /**
   * The size of the toggle
   */
  size: ToggleSize;
  /**
   * Whether the is in an error state
   */
  hasError?: boolean;
  /**
   * Icon to render on the left side of the toggle
   */
  leftIcon?: {
    /**
     * The icon component
     */
    Component: IconComponentType;
    /**
     * The icon label (for screen readers)
     */
    label: string;
  };
  /**
   * Icon to render on the right side of the toggle
   */
  rightIcon?: {
    /**
     * The icon component
     */
    Component: IconComponentType;
    /**
     * The icon label (for screen readers)
     */
    label: string;
  };
  /**
   * Label to render on the left side of the toggle
   */
  leftLabel?: string;
  /**
   * Label to render on the right side of the toggle
   */
  rightLabel?: string;
  /**
   * Is embedded in a text input
   */
  isEmbeddedInTextInput?: boolean;
};

/**
 * A component that lets you toggle between on/off states.
 */
export const Toggle = forwardRef<RNView, ToggleProps>(function Toggle(
  { leftIcon, rightIcon, size, hasError, leftLabel, rightLabel, isEmbeddedInTextInput, ...props },
  ref,
) {
  const [checked, setChecked] = useState(props.defaultChecked || false);

  // See: https://tamagui.dev/docs/components/switch/1.89.0?type=headless
  const { switchProps, switchRef, bubbleInput } = useSwitch(props, [checked, setChecked], ref);
  const { onCheckedChange, ...restSwitchProps } = switchProps;

  useEffect(() => {
    onCheckedChange?.(checked);
    /* We need to make sure we are firing onCheckedChange only on User interaction with toggle / 
    only if the toggle state changes */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const animation = useRef(new Animated.Value(checked ? 1 : 0));

  useEffect(() => {
    Animated.timing(animation.current, {
      toValue: checked ? 1 : 0,
      duration: 80,
      useNativeDriver: true,
    }).start();
  }, [checked]);

  const getIconColor = useCallback(
    (isSelected: boolean) => {
      if (props.disabled) {
        return isSelected
          ? toggleTokens.color.content.element.toggle.state.inactiveOn
          : toggleTokens.color.content.element.toggle.state.inactiveOff;
      }

      if (hasError && !isSelected) {
        return toggleTokens.color.content.element.toggle.state.error;
      }

      return isSelected
        ? toggleTokens.color.content.element.toggle.on
        : toggleTokens.color.content.element.toggle.off;
    },
    [props.disabled, hasError],
  );

  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);

  return (
    <>
      <Frame
        ref={switchRef}
        {...restSwitchProps}
        disabled={!!switchProps.disabled}
        frameSize={size}
        hasError={hasError}
        embedded={isEmbeddedInTextInput}
      >
        <Animated.View
          style={[
            {
              position: 'absolute',
              width: checked ? rightWidth : leftWidth,
              transform: [
                {
                  translateX: animation.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, leftWidth],
                  }),
                },
              ],
            },
          ]}
        >
          <Thumb
            frameSize={size}
            hasError={hasError}
            disabled={props.disabled}
            embedded={isEmbeddedInTextInput}
          />
        </Animated.View>

        <Content
          frameSize={size}
          embedded={isEmbeddedInTextInput}
          onLayout={(e) => setLeftWidth(e.nativeEvent.layout.width)}
          paddingHorizontal={leftLabel ? '$9x' : '$4x'}
        >
          {rightLabel ? (
            <Text
              tag="span"
              typography={
                size === 'small'
                  ? toggleTokens.typography.element.smallSize
                  : toggleTokens.typography.element.mediumSize
              }
              color={getIconColor(!checked)}
            >
              {leftLabel}
            </Text>
          ) : (
            leftIcon?.Component && (
              <leftIcon.Component aria-label={leftIcon.label} color={getIconColor(!checked)} />
            )
          )}
        </Content>

        <Content
          frameSize={size}
          embedded={isEmbeddedInTextInput}
          onLayout={(e) => setRightWidth(e.nativeEvent.layout.width)}
          paddingHorizontal={rightLabel ? '$9x' : '$4x'}
        >
          {rightLabel ? (
            <Text
              tag="span"
              typography={
                size === 'small'
                  ? toggleTokens.typography.element.smallSize
                  : toggleTokens.typography.element.mediumSize
              }
              color={getIconColor(checked)}
            >
              {rightLabel}
            </Text>
          ) : (
            rightIcon?.Component && (
              <rightIcon.Component aria-label={rightIcon.label} color={getIconColor(checked)} />
            )
          )}
        </Content>
      </Frame>
      {bubbleInput}
    </>
  );
});
